import * as Yup from 'yup';
import { useFieldArray } from 'react-hook-form';
import { Button, Card, CardContent, Dialog, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { useReactHookForm } from '../../../hooks/useReactHookForm';
import { ReactHookForm } from '../../../components/hook-form/react-hook-form.component';
import { FormInput } from '../../../components/hook-form/form-input.component';
import { useSaveData } from '../../../hooks/useSaveData';
import { projectsApiKeyStore } from '../../../api/projects-api-key-store';
import { DeleteButton } from '../../../components/delete-button.component';
import { Icons } from '../../../assets/icons';
import { FormAutoCompleteSelectDynamic } from '../../../components/hook-form/form-autocomplete-select.dynamic.component';
import { stateApiKeyStore } from '../../../api/states-api-key-store';
import { FormAutoCompleteMultiSelectDynamic } from '../../../components/hook-form/form-autocomplete-multi-select.dynamic.component';
import { municipalityApiKeyStore } from '../../../api/masters/municipality-api-key-store';
import { FormFormattedInput } from '../../../components/hook-form/form-formatted-input.component';
import { ZIPCODE_FORMAT } from '../../../constants';
import { FormSelectDynamic } from '../../../components/hook-form/form-select.dynamic.component';
import { countyApiKeyStore } from '../../../api/county-api-key-store';
import { CreateScatteredUnitButton } from './create-scattered-unit-button';
import { useEffect, useRef, useState } from 'react';
import { LoadingIndicator } from '../../../components/loading-indicator.component';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { ApiErrorComponent } from '../../../components/api-error.component';
import { proctorResultApiKeyStore } from '../../../api/proctor-result-api-key-store';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { UserPermissionName } from '../../../models/user-permission-name';
import { GuardedComponent } from '../../../components/guarded-component';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { useAuthorize } from '../../../hooks/use-authorize';
import { FormCheckbox } from '../../../components/hook-form/form-checkbox.component';
import { useSnackbar } from '../../../hooks/use-snackbar.hook';
import { parseErrorMessageRecursive } from '../../../utils/yup-error-parser.util';
import { FormSelect } from '../../query-builder/hooks-form-querybuilder/form-select.component';
import { getAddressAbbreviationError } from '../../../utils/abbreviations.util';

const lotTypeOption = [
  { value: 'TH', label: 'Townhome (TH)' },
  { value: 'SFR', label: 'Single-Family Residential (SFR)' },
];

const lotTypeCMOption = [
  { value: 'CM', label: 'Commercial (CM)' },
];

interface BuildingUnitsListFormProps {
  projectID: number;
  singleOrMultiLots: any[];
  isTimeAndMaterial: boolean;
  page?: string;
  syncWithDux: any;
  refetch: () => void;
  projectTypeValue: any;
}

const lotValidationSchema = Yup.object().shape({
  unitName: Yup.string().required('Lot/Unit is required').max(9, 'Lot/Unit cannot exceed 9 characters'),
  address: Yup.string().required('Address is required').max(31, 'Address cannot exceed 31 characters'),
  jobsite: Yup.string().required('Jobsite is required'),
  lotType: Yup.string()
    .nullable()
    .when('isUnitCreationCompleted', ([isUnitCreationCompleted], schema) => {
      return isUnitCreationCompleted !== true
        ? schema.required('Lot Type is required')
        : schema.notRequired();
    }),
});

const timeAndMaterialValidationSchema = Yup.object().shape({
  singleOrMultiLots: Yup.array().of(lotValidationSchema),
});

export const SactteredLoTEditForm = ({
  projectID,
  isTimeAndMaterial,
  singleOrMultiLots,
  page,
  syncWithDux,
  refetch,
  projectTypeValue,
}: BuildingUnitsListFormProps) => {
  const form = useReactHookForm({
    defaultValues: {
      projectID,
      singleOrMultiLots: singleOrMultiLots,
    },
    yupSchema: timeAndMaterialValidationSchema,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'singleOrMultiLots',
    keyName: 'tID',
  });

  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { isLoading, mutateAsync } = useSaveData(projectsApiKeyStore.updateLotInfo());

  // ── Refs ──────────────────────────────────────────────────────────────────
  const initialJobsites = useRef<string[]>(fields?.map((field: any) => field.jobsite || ''));
  const previousLotTypes = useRef<Record<number, string | null>>({});
  // ✅ Tracks the original server-saved lot type — never mutated during user interaction
  const originalLotTypes = useRef<Record<number, string | null>>({});
  const lotTypeChangedFlags = useRef<Record<number, boolean>>({});

  // ── State ─────────────────────────────────────────────────────────────────
  const [openDialog, setOpenDialog] = useState(false);
  const [transformedData, setTransformedData] = useState<any>({});
  const [lotTypeChangeDialog, setLotTypeChangeDialog] = useState<{
    open: boolean;
    index: number;
    newValue: string;
    previousValue: string;
  }>({ open: false, index: -1, newValue: '', previousValue: '' });
  const [lotTypePricingFlags, setLotTypePricingFlags] = useState<Record<number, boolean>>({});

  const watchFields = form.watch();
  const { data } = useLoadData<ApiResponse<any>>(proctorResultApiKeyStore.getProjectProctorResults(projectID));

  // ── Sync form when parent data changes ────────────────────────────────────
  useEffect(() => {
    form.setValue('singleOrMultiLots', singleOrMultiLots);
  }, [singleOrMultiLots]);

  // ── Initialise previousLotTypes + originalLotTypes refs when fields load ──
  // Store null explicitly so the handler can distinguish:
  //   undefined → row never initialised
  //   null      → row loaded from server with no lot type
  //   string    → row has a real saved lot type
  useEffect(() => {
    fields?.forEach((field: any, index: number) => {
      if (previousLotTypes.current[index] === undefined) {
        const currentValue = form.getValues(`singleOrMultiLots.${index}.lotType`);
        previousLotTypes.current[index] = currentValue ?? null;
        // ✅ originalLotTypes is seeded once and never overwritten during user interaction
        originalLotTypes.current[index] = currentValue ?? null;
      }
    });
  }, [fields]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const normalize = (text: string) => text?.toLowerCase()?.trim()?.replace(/\s+/g, '');

  const handleLoadCreated = (lot: any) => {
    const transformedLot = {
      ...lot,
      isUnitCreationCompleted: true,
      stateID: !lot?.stateID ? undefined : { stateID: lot?.stateID || 0 },
      municipalityID: !lot?.municipalityID ? undefined : { municipalityID: lot?.municipalityID || 0 },
    };
    append(transformedLot);
    initialJobsites.current.push(lot.jobsite || '');
  };

  const handleRemove = (index: number) => {
    remove(index);
    initialJobsites.current.splice(index, 1);
  };

  // ── Lot type change handler ───────────────────────────────────────────────
  /**
   * previousLotTypes.current[index] — tracks the last-picked value (updates on every change)
   * originalLotTypes.current[index]  — tracks the original server-saved value (never changes mid-session)
   *
   * Dialog rules:
   *   - same value as last pick                         → no-op, NO dialog
   *   - isUnitCreationCompleted = false / null          → unit not yet saved, NO dialog, silent apply
   *   - original was null (first-time pick)             → NO dialog, silent apply
   *   - new value === original value (reverted back)    → NO dialog, silent apply, reset flag
   *   - new value !== original AND unit already created → SHOW pricing confirmation dialog
   */
  const handleLotTypeChange = (index: number, newValue: string) => {
    const previousValue = previousLotTypes.current[index]; // null | string | undefined
    // ✅ Compare against the original server-saved value, not the last-picked value
    const originalValue = originalLotTypes.current[index]; // null | string

    const isSameAsPrevious = previousValue === newValue;
    // ✅ If the user picks back the original saved value → treat as reverted, no dialog
    const isRevertedToOriginal = !!originalValue && originalValue === newValue;

    // ✅ Only show the dialog when the unit is already created (saved to server)
    const isUnitCreationCompleted = form.getValues(`singleOrMultiLots.${index}.isUnitCreationCompleted`);

    if (isSameAsPrevious) {
      // Exact same value as the last pick → silent no-op
      return;
    }

    if (!isUnitCreationCompleted || !originalValue || isRevertedToOriginal) {
      // Unit not yet created, OR original was null (first-time pick),
      // OR user reverted back to the original saved value → silent apply, no dialog
      lotTypeChangedFlags.current[index] = false;
      previousLotTypes.current[index] = newValue;
      form.setValue(`singleOrMultiLots.${index}.lotType`, newValue);
      // ✅ When reverting to original, reset both flags so submit sends false/false
      if (isRevertedToOriginal) {
        form.setValue(`singleOrMultiLots.${index}.lotTypeChanged`, false);
        form.setValue(`singleOrMultiLots.${index}.isApplyAppropriatePLConfirmed`, false);
      }
    } else {
      // Real change on an already-created unit away from its original value → show dialog
      lotTypeChangedFlags.current[index] = true;
      setLotTypeChangeDialog({ open: true, index, newValue, previousValue: previousValue as string });
    }
  };

  /**
   * User clicked "Yes" — apply new lot type + mark pricing confirmed
   * lotTypeChanged: true | isApplyAppropriatePLConfirmed: true
   */
  const handleLotTypeConfirmYes = () => {
    const { index, newValue } = lotTypeChangeDialog;
    form.setValue(`singleOrMultiLots.${index}.lotType`, newValue);
    form.setValue(`singleOrMultiLots.${index}.isApplyAppropriatePLConfirmed`, true);
    previousLotTypes.current[index] = newValue;
    setLotTypePricingFlags((prev) => ({ ...prev, [index]: true }));
    setLotTypeChangeDialog({ open: false, index: -1, newValue: '', previousValue: '' });
  };

  /**
   * User clicked "No" — apply new lot type but skip pricing update
   * lotTypeChanged: true | isApplyAppropriatePLConfirmed: false
   */
  const handleLotTypeConfirmNo = () => {
    const { index, newValue } = lotTypeChangeDialog;
    form.setValue(`singleOrMultiLots.${index}.lotType`, newValue);
    form.setValue(`singleOrMultiLots.${index}.isApplyAppropriatePLConfirmed`, false);
    previousLotTypes.current[index] = newValue;
    setLotTypePricingFlags((prev) => ({ ...prev, [index]: false }));
    setLotTypeChangeDialog({ open: false, index: -1, newValue: '', previousValue: '' });
  };

  // ── Data transformation before submit ─────────────────────────────────────
  const transformData = (values: any) => ({
    ...values,
    singleOrMultiLots: values.singleOrMultiLots.map((lot: any, index: number) => ({
      ...lot,
      stateID: lot.stateID?.stateID ?? 0,
      municipalityID: lot.municipalityID?.municipalityID ?? 0,
      squareFeet: !lot?.squareFeet ? null : Number(lot?.squareFeet),
      lotTypeChanged: lotTypeChangedFlags.current[index] ?? false,
      isApplyAppropriatePLConfirmed: lot?.isApplyAppropriatePLConfirmed ?? false,
      proctorResult: !lot?.proctorResult
        ? null
        : {
            proctorResultsID: lot.proctorResult.proctorResultsID,
            unitID: lot.unitID,
            proctorFileName: lot.proctorResult.proctorFileName,
          },
    })),
  });

  // ── Submit handler ────────────────────────────────────────────────────────
  const handleSubmit = async (values: any) => {
    const transformed = transformData(values);
    const { type, ...dataWithoutType } = transformed;

    const hasCreateWOTrue = transformed?.singleOrMultiLots?.some((item: any) => item?.createWO === true);

    if (transformed?.singleOrMultiLots?.length > 0 && !hasCreateWOTrue && type === 'with-workorder') {
      showSnackbar(`Please select at least one 'Create Work Order' before saving.`, 'error');
      return;
    }
    if (hasCreateWOTrue && type === 'without-workorder') {
      showSnackbar(`Please do not select 'Create Work Order' before saving.`, 'error');
      return;
    }

    await mutateAsync({ data: { ...dataWithoutType, generateWorkOrders: type === 'with-workorder' } }).then(
      (data: any) => {
        const lots = data?.data?.singleOrMultiLots;
        form.setValue('singleOrMultiLots', lots);

        // Reset all tracking refs after a successful save
        setLotTypePricingFlags({});
        lotTypeChangedFlags.current = {};
        lots?.forEach((lot: any, index: number) => {
          previousLotTypes.current[index] = lot.lotType ?? null;
          // ✅ After save the new lot type becomes the new "original" for future edits
          originalLotTypes.current[index] = lot.lotType ?? null;
        });
        initialJobsites.current = lots?.map((field: any) => field.jobsite || '');

        refetch();
      }
    );
  };

  // ── Duplicate / abbreviation validators ──────────────────────────────────
  const handleUnitNameChange = (index: number, value: string) => {
    const initialJobsite = initialJobsites?.current[index];
    const newJobsite = `${initialJobsite}${value}`?.trim();
    form.setValue(`singleOrMultiLots.${index}.jobsite`, newJobsite);

    if (!value || value?.trim() === '') {
      form.clearErrors(`singleOrMultiLots.${index}.unitName`);
      return;
    }

    const allUnits = form.getValues('singleOrMultiLots')?.map((u: any) => normalize(u?.unitName));
    const isDuplicate = allUnits?.filter((u: string) => u === normalize(value))?.length > 1;

    if (isDuplicate) {
      form.setError(`singleOrMultiLots.${index}.unitName`, {
        type: 'manual',
        message: 'Lot/Unit name already exists.',
      });
    } else {
      form.clearErrors(`singleOrMultiLots.${index}.unitName`);
    }
  };

  const handleJobNumberChange = (index: number, value: string) => {
    if (!value || value?.trim() === '') {
      form.clearErrors(`singleOrMultiLots.${index}.jobNumber`);
      return;
    }

    const allUnits = form.getValues('singleOrMultiLots')?.map((u: any) => normalize(u?.jobNumber));
    const isDuplicate = allUnits?.filter((u: string) => u === normalize(value))?.length > 1;

    if (isDuplicate) {
      form.setError(`singleOrMultiLots.${index}.jobNumber`, {
        type: 'manual',
        message: 'Job number already exists.',
      });
    } else {
      form.clearErrors(`singleOrMultiLots.${index}.jobNumber`);
    }
  };

  const handleAddressChange = (index: number, value: string) => {
    if (!value || value?.trim() === '') {
      form.clearErrors(`singleOrMultiLots.${index}.address`);
      return;
    }

    const abbrError = getAddressAbbreviationError(value);
    const allUnits = form.getValues('singleOrMultiLots')?.map((u: any) => normalize(u?.address));
    const isDuplicate = allUnits?.filter((u: string) => u === normalize(value))?.length > 1;

    if (abbrError) {
      form.setError(`singleOrMultiLots.${index}.address`, { type: 'manual', message: abbrError });
    } else if (isDuplicate) {
      form.setError(`singleOrMultiLots.${index}.address`, {
        type: 'manual',
        message: 'Address already exists.',
      });
    } else {
      form.clearErrors(`singleOrMultiLots.${index}.address`);
    }
  };

  const handleJobnameNameChange = (index: number, value: any) => {
    initialJobsites.current[index] = value;

    if (!value || value?.trim() === '') {
      form.clearErrors(`singleOrMultiLots.${index}.jobsite`);
      return;
    }

    const allUnits = form.getValues('singleOrMultiLots')?.map((u: any) => normalize(u?.jobsite));
    const isDuplicate = allUnits?.filter((u: string) => u === normalize(value))?.length > 1;

    if (isDuplicate) {
      form.setError(`singleOrMultiLots.${index}.jobsite`, {
        type: 'manual',
        message: 'Jobsite already exists.',
      });
    } else {
      form.clearErrors(`singleOrMultiLots.${index}.jobsite`);
    }
  };

  // ── Work-order dialog handlers ────────────────────────────────────────────
  const closeDialog = async () => {
    const { type, ...dataWithoutType } = transformedData;
    await mutateAsync({ data: { ...dataWithoutType, generateWorkOrders: false } });
    setOpenDialog(false);
  };

  const handleSubmitConfirm = async () => {
    const { type, ...dataWithoutType } = transformedData;
    await mutateAsync({ data: { ...dataWithoutType, generateWorkOrders: true } });
    setOpenDialog(false);
  };

  // ── Validation error display ──────────────────────────────────────────────
  const handleError = (error: any) => {
    const messages = parseErrorMessageRecursive(error);
    let requiredMessages: any[] = [];
    let otherMessages: any[] = [];

    Array.isArray(messages) &&
      messages?.forEach((message) => {
        if (message.endsWith('is required')) {
          requiredMessages.push(message?.split(' is required')[0]?.trim());
        } else {
          otherMessages.push(message.trim());
        }
      });

    requiredMessages = Array.from(new Set(requiredMessages));
    otherMessages = Array.from(new Set(otherMessages));

    const finalRequiredMessage = requiredMessages.length > 0 ? `${requiredMessages?.join(', ')} is required` : '';
    const finalOtherMessage = otherMessages?.join(', ');
    const finalMessage = [finalRequiredMessage, finalOtherMessage]?.filter(Boolean)?.join(', ');

    showSnackbar(`Validation Error: ${finalMessage}`, 'error');
  };

  const onTypedSubmit = async (type: string) => {
    form.handleSubmit((values) => handleSubmit({ ...values, type }), handleError)();
  };

  const disabledBuildingPermit = (index: number) => {
    return form.getValues('singleOrMultiLots')[index]?.projectSyncWithDUX;
  };

  const isProjectTypeRES = true;
  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      <ReactHookForm
        formName="TIME_AND_MATERIAL_FORM"
        onSubmit={handleSubmit}
        formContextProps={form}
        isBusy={isLoading}
      >
        <Card elevation={0}>
          <CardContent style={{ padding: '25px', paddingTop: '30px' }}>
            <Grid container spacing={2}>
              {fields.map((field, index) => (
                <Grid key={field.tID} item lg={12} sm={12} xl={12} xs={12}>
                  <Grid container spacing={2}>
                    <Grid item lg={8} sm={8} xl={8} xs={8}>
                      <div style={{ display: 'none' }}>
                        <label>{form.getValues(`singleOrMultiLots.${index}.unitNumber`)}</label>
                      </div>
                      <Grid container spacing={2}>
                        <Grid item lg={3} sm={3} xl={3} xs={3}>
                          <FormInput
                            name={`singleOrMultiLots.${index}.jobsite`}
                            label={isTimeAndMaterial ? 'Inspection Title' : 'Jobsite'}
                            isCustomError={true}
                            validationQueryRoute={(value: string) =>
                              projectsApiKeyStore.checkDuplicateLotAddressJobsite(
                                projectID,
                                '',
                                form.getValues(`singleOrMultiLots.${index}.unitID`),
                                '',
                                value
                              )
                            }
                            onChange={(e: any) => handleJobnameNameChange(index, e)}
                          />
                        </Grid>
                        <Grid item lg={3} sm={3} xl={3} xs={3}>
                          <FormInput
                            name={`singleOrMultiLots.${index}.unitName`}
                            label={'Lot/Unit'}
                            isCustomError={true}
                            validationQueryRoute={(value: string) =>
                              projectsApiKeyStore.checkDuplicateLotAddressUnitName(
                                projectID,
                                '',
                                form.getValues(`singleOrMultiLots.${index}.unitID`),
                                value,
                                ''
                              )
                            }
                            onChange={(e: any) => handleUnitNameChange(index, e)}
                          />
                        </Grid>
                        <Grid item lg={3} sm={3} xl={3} xs={3}>
                          <FormInput
                            name={`singleOrMultiLots.${index}.jobNumber`}
                            label={'Builder Job Number'}
                            isCustomError={true}
                            onChange={(e: any) => handleJobNumberChange(index, e)}
                            validationQueryRoute={(value: string) =>
                              projectsApiKeyStore.checkDuplicateLotAddressJobNumber(
                                projectID,
                                '',
                                form.getValues(`singleOrMultiLots.${index}.unitID`),
                                '',
                                '',
                                value
                              )
                            }
                          />
                        </Grid>
                        <Grid item lg={3} sm={3} xl={3} xs={3}>
                          <FormInput
                            name={`singleOrMultiLots.${index}.address`}
                            label="Address"
                            onChange={(e: any) => handleAddressChange(index, e)}
                            isCustomError={true}
                            validationQueryRoute={(value: string) =>
                              projectsApiKeyStore.checkDuplicateLotAddressOfAddress(
                                projectID,
                                value,
                                form.getValues(`singleOrMultiLots.${index}.unitID`)
                              )
                            }
                          />
                        </Grid>
                        <Grid item lg={3} sm={3} xl={3} xs={3}>
                          <FormInput name={`singleOrMultiLots.${index}.city`} label="City" />
                        </Grid>
                        <Grid item lg={3} sm={3} xl={3} xs={3}>
                          <FormAutoCompleteSelectDynamic
                            name={`singleOrMultiLots.${index}.stateID`}
                            label="State"
                            getQueryKeyConfig={() => stateApiKeyStore.getStates()}
                            optionsValueKey="stateID"
                            optionsLabelKey="stateName"
                          />
                        </Grid>
                        <Grid item lg={3} sm={3} xl={3} xs={3}>
                          <FormFormattedInput
                            name={`singleOrMultiLots.${index}.zipcode`}
                            label="Zip Code"
                            format={ZIPCODE_FORMAT}
                          />
                        </Grid>
                        <Grid item lg={3} sm={3} xl={3} xs={3}>
                          <FormSelectDynamic
                            name={`singleOrMultiLots.${index}.countyID`}
                            label="County"
                            getQueryKeyConfig={() => countyApiKeyStore.getCounties()}
                            optionsValueKey="countyID"
                            optionsLabelKeys={['countyName']}
                          />
                        </Grid>
                        <Grid item lg={3} sm={3} xl={3} xs={3}>
                          <FormAutoCompleteSelectDynamic
                            name={`singleOrMultiLots.${index}.municipalityID`}
                            label="Municipality"
                            disabled={!watchFields?.singleOrMultiLots[index]?.stateID}
                            getQueryKeyConfig={() =>
                              municipalityApiKeyStore.getMunicipalityByStateID(
                                watchFields?.singleOrMultiLots[index]?.stateID?.stateID
                              )
                            }
                            optionsValueKey="municipalityID"
                            optionsLabelKey="muncipalityName"
                          />
                        </Grid>
                        <Grid item lg={3} sm={3} xl={3} xs={3}>
                          <FormInput name={`singleOrMultiLots.${index}.squareFeet`} label={'Square Feet'} />
                        </Grid>
                        <Grid item lg={3} sm={3} xl={3} xs={3}>
                          <FormAutoCompleteSelectDynamic
                            name={`singleOrMultiLots.${index}.proctorResult`}
                            label="Proctor Result"
                            optionsValueKey="proctorResultsID"
                            optionsLabelKey="proctorFileName"
                            dataItems={!data ? [] : data?.data}
                          />
                        </Grid>
                        {isProjectTypeRES && (
                          <Grid item lg={3} sm={3} xl={3} xs={3}>
                            <FormSelect
                              name={`singleOrMultiLots.${index}.lotType`}
                              label="Lot Type"
                              options={projectTypeValue === 'RES' ? lotTypeOption : lotTypeCMOption}
                              error={true}
                              onChange={(e: any) => handleLotTypeChange(index, e)}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </Grid>

                    <Grid item lg={4} sm={4} xl={4} xs={4}>
                      <Grid
                        container
                        direction="row"
                        sx={{ justifyContent: 'flex-start', alignItems: 'flex-start' }}
                      >
                        <FormCheckbox label="Create WO" name={`singleOrMultiLots.${index}.createWO`} />
                        <FormCheckbox
                          label="Sync With DUX"
                          name={`singleOrMultiLots.${index}.syncWithDUX`}
                          disabled={!disabledBuildingPermit(index)}
                        />
                        <FormInput
                          label="Building Permit#"
                          name={`singleOrMultiLots.${index}.buildingPermit`}
                          styleField={{ width: '200px' }}
                        />
                        {page && form.getValues(`singleOrMultiLots.${index}.isUnitCreationCompleted`) && (
                          <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_UNIT]}>
                            <IconButton
                              style={{ padding: '10px 2px 5px 15px' }}
                              aria-label="view"
                              onClick={() =>
                                navigate(`/units/profile/${form.getValues(`singleOrMultiLots.${index}.unitID`)}`)
                              }
                            >
                              <Tooltip title={<Typography variant="body1">View</Typography>}>
                                <VisibilityIcon className="btnView" />
                              </Tooltip>
                            </IconButton>
                          </GuardedComponent>
                        )}
                        <GuardedComponent requiredPermissions={[UserPermissionName.DELETE_UNIT]}>
                          {!form.getValues(`singleOrMultiLots.${index}.disableDelete`) ? (
                            <DeleteButton
                              data={undefined}
                              deleteMutationConfig={() =>
                                projectsApiKeyStore.deleteLot(form.getValues(`singleOrMultiLots.${index}.unitID`))
                              }
                              onDeleted={() => handleRemove(index)}
                            >
                              <>
                                <Tooltip title={<Typography variant="body1">Delete</Typography>}>
                                  <img src={Icons.Delete} alt="DELETE" />
                                </Tooltip>
                              </>
                            </DeleteButton>
                          ) : (
                            <DeleteButton
                              data={undefined}
                              deleteMutationConfig={() =>
                                projectsApiKeyStore.deleteLot(form.getValues(`singleOrMultiLots.${index}.unitID`))
                              }
                              onDeleted={() => handleRemove(index)}
                              isDisabled={true}
                            >
                              <>
                                <Tooltip title={<Typography variant="body1">Delete</Typography>}>
                                  <img src={Icons.DisabledDelete} alt="DELETE" />
                                </Tooltip>
                              </>
                            </DeleteButton>
                          )}
                        </GuardedComponent>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </CardContent>

          <Grid item lg={1} sm={1} xl={1} xs={1}>
            <CreateScatteredUnitButton
              projectID={projectID}
              projectTypeValue={projectTypeValue}
              onUnitCreated={handleLoadCreated}
              floor={{ floorID: undefined }}
              syncWithDux={syncWithDux}
              existingData={form.getValues('singleOrMultiLots')}
            />
          </Grid>

          {/* ── Work Order Confirmation Dialog ────────────────────────────── */}
          {openDialog && (
            <Dialog open={openDialog} className="modalpopup" title={'CONFIRM STATUS'}>
              <div>
                <div style={{ padding: '25px 50px', textAlign: 'center' }}>
                  <div>Are you sure you want to generate work orders for all the newly added Lot/Units?</div>
                </div>
              </div>
              <DialogActionsBar>
                {isReadOnlyPermission && (
                  <Button
                    style={{ maxWidth: '140px' }}
                    size="small"
                    type="button"
                    variant="outlined"
                    onClick={() => setOpenDialog(false)}
                  >
                    Cancel
                  </Button>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', padding: 2 }} className="  ">
                  <button
                    className="btn btn-sm btn-primary btn-lightGrey"
                    style={{
                      backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#0000b5',
                      color: isReadOnlyPermission ? 'black' : '#fff',
                      cursor: 'pointer',
                      padding: 2,
                      marginRight: 6,
                    }}
                    onClick={closeDialog}
                    autoFocus
                    disabled={isReadOnlyPermission}
                  >
                    No
                  </button>
                  <button
                    className="btn btn-sm btn-primary btn-delete"
                    style={{
                      backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#0000b5',
                      color: isReadOnlyPermission ? 'black' : '#fff',
                      cursor: 'pointer',
                      padding: 2,
                    }}
                    type="button"
                    onClick={handleSubmitConfirm}
                    disabled={isLoading || isReadOnlyPermission}
                  >
                    Yes
                  </button>
                </div>
              </DialogActionsBar>
            </Dialog>
          )}
        </Card>

        <GuardedComponent requiredPermissions={[UserPermissionName.ADD_UNIT]}>
          <Grid
            item
            lg={12}
            sm={12}
            xl={12}
            xs={12}
            style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}
          >
            <Button
              type="button"
              className="btn btn-primary btnIcon"
              variant="text"
              style={{ backgroundColor: '#e67e22' }}
              sx={{ p: 0, m: 0, mr: 1 }}
              onClick={() => onTypedSubmit('without-workorder')}
            >
              Create Lots/Units without workorders
            </Button>
            <Button
              type="button"
              className="btn btn-primary btnIcon"
              variant="text"
              style={{ backgroundColor: '#239b56' }}
              sx={{ p: 0, m: 0, mr: 1 }}
              onClick={() => onTypedSubmit('with-workorder')}
            >
              Create Lots/Units with workorders
            </Button>
          </Grid>
        </GuardedComponent>
      </ReactHookForm>

      {/* ── ✅ Lot Type Pricing Confirmation Dialog ────────────────────────── */}
      {/* Only shown when isUnitCreationCompleted = true AND lot type changed from original */}
      {lotTypeChangeDialog.open && (
        <Dialog open={lotTypeChangeDialog.open} className="modalpopup">
          <div style={{ padding: '25px 50px', textAlign: 'center' }}>
            <Typography variant="body1">
              Do you want to update the pricing for this lot&apos;s work orders based on the selected lot type
              change?
            </Typography>
          </div>
          <DialogActionsBar>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', padding: '8px' }}>
              <button
                className="btn btn-sm btn-primary"
                style={{ backgroundColor: '#0000b5', color: '#fff', cursor: 'pointer', padding: '6px 16px' }}
                onClick={handleLotTypeConfirmYes}
              >
                Yes
              </button>
              <button
                className="btn btn-sm btn-primary btn-lightGrey"
                style={{ backgroundColor: '#d7dbdd', color: 'black', cursor: 'pointer', padding: '6px 16px' }}
                onClick={handleLotTypeConfirmNo}
              >
                No
              </button>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};